// Field "More" menu — reached via the avatar button in MobileShell's header
// (previously a Stub placeholder with no actual links, so screens like
// "My day summary" had no way in except a notification deep-link).
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Target, CalendarCheck, Monitor, ClipboardList, KeyRound, ChevronRight, LogOut, ClipboardCheck,
         Store, Boxes, Wallet, MapPin, ShoppingBag, BarChart3, FileCheck2,
         Truck, MessagesSquare, Mail } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { BROADCASTS_CHANGED_EVENT } from "@/components/BroadcastPopup";

function Row({ icon: Icon, label, sub, onClick, testid, badge }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-neutral-50 active:bg-neutral-100 transition-colors"
    >
      <div className="h-9 w-9 rounded-xl bg-[#6D2C91]/10 text-[#6D2C91] flex items-center justify-center shrink-0">
        <Icon className="h-4.5 w-4.5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-neutral-900">{label}</div>
        {sub && <div className="text-xs text-neutral-500 truncate">{sub}</div>}
      </div>
      {badge > 0 && (
        <span className="min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold text-white bg-[#E5197F] flex items-center justify-center shrink-0"
              data-testid={`${testid}-badge`}>
          {badge > 99 ? "99+" : badge}
        </span>
      )}
      <ChevronRight className="h-4 w-4 text-neutral-300 shrink-0" />
    </button>
  );
}

export default function MoreMenu() {
  const navigate = useNavigate();
  const { user, logout, hasWorkflowPerm, canSwitchView, setViewMode } = useAuth();
  const canPrimaryOrder = hasWorkflowPerm("PRIMARY_ORDER");

  // Unread admin broadcast messages — badge on the "Messages" row.
  const [unreadMessages, setUnreadMessages] = useState(0);
  const loadUnread = useCallback(async () => {
    try {
      const { data } = await api.get("/broadcasts/inbox", { params: { page_size: 1 } });
      setUnreadMessages(data?.unread || 0);
    } catch (_e) { /* badge is optional — stay quiet */ }
  }, []);
  useEffect(() => {
    loadUnread();
    window.addEventListener(BROADCASTS_CHANGED_EVENT, loadUnread);
    return () => window.removeEventListener(BROADCASTS_CHANGED_EVENT, loadUnread);
  }, [loadUnread]);

  const initials = (user?.name || "?")
    .split(" ").map((n) => n[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

  // ASM is a dual-role user: field app by default (visits/orders/attendance/
  // TADA), but they also carry a slice of manager/admin capability (PJP
  // approvals, TADA approvals, location map, retailer list, order grid,
  // reports) — none of those office screens have any other entry point
  // from the field shell, so they all live here.
  // ZSM gets the same manager rows as ASM (same view in both roles).
  const isASM = user?.role_code === "ASM" || user?.role_code === "ZSM";

  return (
    <div className="p-4 pb-32 space-y-4" data-testid="more-menu-root">
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 rounded-full tulsi-gradient text-white flex items-center justify-center text-sm font-bold shrink-0">
          {initials}
        </div>
        <div className="min-w-0">
          <div className="text-lg font-extrabold text-neutral-900 truncate">{user?.name}</div>
          <div className="text-xs text-neutral-500">{user?.role_code?.replace(/_/g, " ")}</div>
        </div>
      </div>

      <Card className="border-neutral-200 rounded-2xl overflow-hidden">
        <CardContent className="p-0 divide-y divide-neutral-100">
          <Row
            icon={Mail}
            label="Messages"
            sub={unreadMessages > 0 ? `${unreadMessages} unread message${unreadMessages === 1 ? "" : "s"} from the office` : "Messages from the office"}
            onClick={() => navigate("/messages")}
            testid="more-messages"
            badge={unreadMessages}
          />
          <Row
            icon={Target}
            label="My targets (kg)"
            sub="Target vs achievement — this month and quarter"
            onClick={() => navigate("/my-targets")}
            testid="more-my-targets"
          />
          {["SE", "ASM", "ZSM"].includes(user?.role_code) && (
            <Row
              icon={BarChart3}
              label="Sales report"
              sub="Beat, retailer and distributor wise sales (kg)"
              onClick={() => navigate("/sales-report")}
              testid="more-sales-report"
            />
          )}
          <Row
            icon={CalendarCheck}
            label="My attendance"
            sub="Whole month — present, half day, leave, km"
            onClick={() => navigate("/attendance-calendar")}
            testid="more-attendance-calendar"
          />
          <Row
            icon={ClipboardList}
            label="My day summary"
            sub="Calls, hours, secondary Kg sold, beat/distributor — day by day"
            onClick={() => navigate("/field/my-day-summary")}
            testid="more-my-day-summary"
          />
          <Row
            icon={FileCheck2}
            label={isASM ? "My & team's activity forms" : "My activity forms"}
            sub="Submitted forms and their verification status"
            onClick={() => navigate("/field/my-activity-submissions")}
            testid="more-my-activity-submissions"
          />
          {canPrimaryOrder && (
            <Row
              icon={Truck}
              label="Primary order"
              sub="Punch a primary order for your mapped distributor"
              onClick={() => navigate("/primary-order")}
              testid="more-primary-order"
            />
          )}
          {["SE", "ASM", "ZSM"].includes(user?.role_code) && (
            <Row
              icon={MessagesSquare}
              label="Distributor queries"
              sub="Raise or track distributor queries"
              onClick={() => navigate("/queries")}
              testid="more-distributor-queries"
            />
          )}
          {isASM && (
            <Row
              icon={ClipboardCheck}
              label="PJP approvals"
              sub="Approve or reject field-initiated beat change requests"
              onClick={() => navigate("/admin/pjp-requests")}
              testid="more-pjp-approvals"
            />
          )}
          <Row
            icon={KeyRound}
            label="Change password"
            onClick={() => navigate("/change-password")}
            testid="more-change-password"
          />
        </CardContent>
      </Card>

      {isASM && (
        <Card className="border-neutral-200 rounded-2xl overflow-hidden">
          <CardContent className="p-0 divide-y divide-neutral-100">
            <Row
              icon={Store}
              label="Retailer list"
              sub="Distributor-wise — view + download"
              onClick={() => navigate("/manager/retailer-list")}
              testid="more-retailer-list"
            />
            <Row
              icon={Boxes}
              label="Distributor stock"
              sub="Check current stock, distributor-wise"
              onClick={() => navigate("/distributor-stock")}
              testid="more-distributor-stock"
            />
            <Row
              icon={Wallet}
              label="TA/DA approvals"
              sub="Approve or reject team's travel/daily allowance claims"
              onClick={() => navigate("/admin/tada-approval")}
              testid="more-tada-approval"
            />
            <Row
              icon={Truck}
              label="Primary sale return approvals"
              sub="Approve or reject distributor stock returns"
              onClick={() => navigate("/admin/primary-sale-return-approval")}
              testid="more-primary-sr-approval"
            />
            <Row
              icon={BarChart3}
              label="Team claims dashboard"
              sub="Total / approved / rejected claim amounts — tap for details"
              onClick={() => navigate("/manager/team-claims")}
              testid="more-team-claims"
            />
            <Row
              icon={MapPin}
              label="Location map"
              sub="Live location of your team"
              onClick={() => navigate("/admin/user-map")}
              testid="more-location-map"
            />
            <Row
              icon={ShoppingBag}
              label="Order grid"
              sub="Edit or reject orders not yet fulfilled"
              onClick={() => navigate("/admin/orders")}
              testid="more-order-grid"
            />
            <Row
              icon={BarChart3}
              label="Reports"
              sub="Order, visit, working, targets, money, inventory…"
              onClick={() => navigate("/manager/reports-hub")}
              testid="more-reports-hub"
            />
          </CardContent>
        </Card>
      )}

      {canSwitchView && (
        <Card className="border-neutral-200 rounded-2xl overflow-hidden">
          <CardContent className="p-0">
            <Row
              icon={Monitor}
              label="Switch to Browser view"
              sub="Full office view with every screen and report you can access"
              onClick={() => { setViewMode("browser"); navigate("/"); }}
              testid="more-switch-browser"
            />
          </CardContent>
        </Card>
      )}

      <Card className="border-neutral-200 rounded-2xl overflow-hidden">
        <CardContent className="p-0">
          <Row
            icon={LogOut}
            label="Log out"
            onClick={logout}
            testid="more-logout"
          />
        </CardContent>
      </Card>
    </div>
  );
}
